import { useMemo } from 'react';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';

export interface PlanLocationData {
  displayServiceAddress?: string;
  stateAbbreviation?: string;
  stateFullName?: string;
  zipCode?: string;
}

interface Props {
  planId?: number;
}

export const usePlanLocation = ({ planId }: Props) => {
  const {
    data: planData,
    error,
    loading,
  } = useQuery<GetUsePlanLocationQuery, GetUsePlanLocationQueryVariables>(
    GET_USE_PLAN_LOCATION_QUERY,
    {
      skip: !planId,
      variables: { id: planId || -1 },
    }
  );

  const data: PlanLocationData | undefined = useMemo(() => {
    if (loading || error || !planData) return undefined;
    const utilityAccount = planData.plan.utilityAccounts[0];
    return {
      displayServiceAddress: utilityAccount?.displayServiceAddress,
      stateAbbreviation:
        utilityAccount?.serviceAddressState ||
        utilityAccount?.utility?.state?.abbreviation,
      stateFullName: utilityAccount?.utility?.state?.name,
      zipCode: utilityAccount?.serviceAddressZip || undefined,
    };
  }, [planData, loading, error]);

  return { data, error, loading };
};

export const GET_USE_PLAN_LOCATION_QUERY = gql`
  query GetUsePlanLocation($id: Int!) {
    plan(id: $id) {
      id
      utilityAccounts {
        id
        displayServiceAddress
        serviceAddressState
        serviceAddressZip
        utility {
          id
          state {
            id
            name
            abbreviation
          }
        }
      }
    }
  }
`;

export const getPlanLocationMock = {
  plan: {
    id: 1,
    utilityAccounts: [
      {
        id: 100,
        displayServiceAddress: '123 Fake St, Falseville, FK 99999',
        serviceAddressState: 'FK',
        serviceAddressZip: '99999',
        utility: {
          id: 2,
          state: {
            abbreviation: 'ZZ',
            id: 50,
            name: 'Zarizona',
          },
        },
      },
    ],
  },
};

export const usePlanLocationMock = {
  data: {
    displayServiceAddress: '123 Fake St, Falseville, FK 99999',
    stateAbbreviation: 'FK',
    stateFullName: 'Zarizona',
    zipCode: '99999',
  },
  loading: false,
  error: undefined,
};
